import { Controller } from "@hotwired/stimulus"
import { showFlashMessage } from '../helpers/flash_helper'
import { FilePreviewBuilder, formatFileSize } from '../helpers/file_preview_helper'

export default class extends Controller {
  static targets = ['preview', 'saveButton', 'input', 'dropzone', 'scrollBody', 'uploadArea'];
  static values = {
    uploadUrl: String,
    acceptedMimeTypes: { type: Array, default: ['image/jpeg', 'image/gif', 'image/png', 'video/mp4', 'audio/mpeg'] }
  }

  connect() {
    this.files = []
    this.boundHandleDroppedFiles = (e) => this.handleDroppedFiles(e)
    this.uploadAreaTarget.addEventListener('dropzone:dropped', (e) => this.boundHandleDroppedFiles(e))

    // Store original button text to restore later
    this.originalButtonText = this.saveButtonTarget.textContent

    $(this.element).on('shown.bs.modal', () => {
      $('.btn').one('focus', () => {
        $('.btn').blur()
      })
    })
  }

  disconnect() {
    this.uploadAreaTarget.removeEventListener('dropzone:dropped', this.boundHandleDroppedFiles)
    $(this.element).off('shown.bs.modal')
    this.files = []
  }

  closeModal() {
    $(this.element).modal('hide')
  }

  closeModalAndShowError(errorMessage) {
    // Close modal first
    $(this.element).one('hidden.bs.modal', () => {
      // Show error after modal is fully closed
      setTimeout(() => {
        showFlashMessage(errorMessage, 'danger')
      }, 100)
    })

    this.closeModal()
    this.saveButtonTarget.disabled = false
    this.saveButtonTarget.textContent = this.originalButtonText
  }

  openFileBrowser() {
    this.inputTarget.click()
  }

  handleDroppedFiles(event) {
    const files = Array.from(event.detail?.dataTransfer?.files || [])
    this.addFiles(files)
  }

  handleFiles(event) {
    const files = Array.from(event.target?.files || [])
    this.addFiles(files)
  }

  addFiles(files) {
    const maxFileSize = 50 * 1024 * 1024 // 50MB
    const rejectedFiles = []
    const invalidFileTypeFiles = []

    files.forEach(file => {
      if (this.isValidFile(file)) {
        if (file.size > maxFileSize) {
          rejectedFiles.push({ name: file.name, size: formatFileSize(file.size) })
        } else {
          this.files.push(file)
          this.createPreview(file)
        }
      } else {
        invalidFileTypeFiles.push(file.name)
      }
    })

    if (rejectedFiles.length > 0) {
      const fileList = rejectedFiles.map(f => `${f.name} (${f.size})`).join('\n')
      alert(`The following files are too large (max 50MB) and will not be uploaded:\n\n${fileList}\n\nPlease resize them before uploading.`)
    }

    if (invalidFileTypeFiles.length > 0) {
      alert(`The following files has invalid media type: ${invalidFileTypeFiles.join('\n')} `)
    }

    this.updateSaveButton()
    this.updateDropzoneHeightAndScroll()
  }

  isValidFile(file) {
    return this.acceptedMimeTypesValue.includes(file.type)
  }

  createPreview(file) {
    new FilePreviewBuilder(file, this.previewTarget).build()
  }

  removeFile(event) {
    const previewItem = event.target.closest('.file-preview-item')
    const fileName = previewItem.dataset.fileName

    this.files = this.files.filter(f => f.name !== fileName)
    previewItem.remove()
    this.updateSaveButton()
    this.updateDropzoneHeightAndScroll()
  }

  updateSaveButton() {
    this.saveButtonTarget.disabled = this.files.length === 0
    this.saveButtonTarget.textContent = this.originalButtonText
  }

  async saveFiles() {
    if (this.files.length === 0) return

    this.setUploadingState()

    try {
      const response = await this.uploadFiles()
      const data = await response.json()

      response.ok ? this.handleSuccess(data) : this.handleFailure(data)
    } catch (error) {
      this.handleNetworkError(error)
    }
  }

  setUploadingState() {
    this.saveButtonTarget.disabled = true
    this.saveButtonTarget.textContent = 'Uploading...'
  }

  async uploadFiles() {
    const formData = this.buildFormData()

    return fetch(this.uploadUrlValue, {
      method: 'POST',
      body: formData,
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      }
    })
  }

  buildFormData() {
    const formData = new FormData()

    this.files.forEach((file) => {
      formData.append(`files[]`, file)
      formData.append(`keep_original[]`, this.getKeepOriginalFlag(file))
    })

    return formData
  }

  getKeepOriginalFlag(file) {
    if (!file.type.startsWith('image/')) return 'true'

    const previewItem = this.previewTarget.querySelector(`[data-file-name="${file.name}"]`)
    const keepOriginal = previewItem?.querySelector('.keep-original-checkbox')?.checked || false
    return keepOriginal.toString()
  }

  handleSuccess(data) {
    this.showSuccessMessage(data)
    this.handlePostUpload()
  }

  showSuccessMessage(data) {
    if (data.failed && data.failed.length > 0) {
      const failedNames = data.failed.map(f => f.name).join(', ')
      showFlashMessage(
        `${data.count} file(s) uploaded successfully, but ${data.failed.length} failed: ${failedNames}`,
        'warning'
      )
    } else {
      showFlashMessage(data.message || `${data.count} file(s) uploaded successfully`, 'success')
    }
  }

  handlePostUpload() {
    const isModal = document.querySelector('[data-media-library-modal-target="mediaLibraryTabLink"]')

    if (isModal) {
      this.cancel()
      this.dispatch('success', { bubbles: true })
    } else {
      window.location.reload()
    }
  }

  handleFailure(data) {
    const errorMessage = data.error || 'Upload failed. Please try again.'
    this.closeModalAndShowError(errorMessage)
  }

  handleNetworkError(error) {
    console.error('Upload error:', error)
    this.closeModalAndShowError('Upload failed due to a network error. Please try again.')
  }

  cancel() {
    this.files = []
    this.previewTarget.innerHTML = ''
    const fileInput = this.element.querySelector('input[type="file"]')
    if (fileInput) {
      fileInput.value = ''
    }
    this.updateSaveButton()
    this.updateDropzoneHeightAndScroll()
  }

  updateDropzoneHeightAndScroll() {
    if (this.files.length > 0) {
      this.dropzoneTarget.classList.add('has-files')
      setTimeout(() => {
        this.scrollBodyTarget.scrollTo({ top: this.scrollBodyTarget.scrollHeight, behavior: 'smooth' })
      }, 100)
    } else {
      this.dropzoneTarget.classList.remove('has-files')
    }
  }
}
